import React from "react";
import { ArrowUpRight } from "@dreamhub/designsystem/icons";
import { useNavigate } from "react-router-dom";
import { APIObject } from "@/services/api/types";
import { CellContext } from "@tanstack/react-table";
import {
  EntityType,
  entityTypeToEntityName
} from "@/services/hooks/entities/useEntityType";
import { useDealsNavigation } from "@/services/hooks/entities/deal/useDealsNavigation";
import { IconButton } from "@/components/ui/icon-button";
import { upperFirst } from "lodash";
import ClickableLink from "@/components/ui/clickable-link";

interface Props {
  info: CellContext<any, unknown>;
  entityType: EntityType;
}

export const TableNameCell: React.FC<Props> = ({ info, entityType }) => {
  const navigate = useNavigate();
  const { navigateToDetailedView: navigateToDealDetailedView } =
    useDealsNavigation();
  const rowData = info.row.original as APIObject;
  const url = `/${entityTypeToEntityName[entityType]}/${rowData.id}`;

  return (
    <div className="font-semibold cursor-pointer flex items-center justify-between w-full group/cell">
      <ClickableLink
        href={url}
        onClick={(e) => {
          // For left-click: prevent default and do nothing (let row handle it)
          // For right-click/modified clicks: stop propagation and navigate
          if (e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey) {
            // Left-click: prevent navigation, let row click handle it
            e.preventDefault();
          } else {
            // Right-click or modified click: stop propagation and navigate
            e.stopPropagation();
            if (entityType === EntityType.DEAL) {
              navigateToDealDetailedView(rowData.id);
            } else {
              navigate(url);
            }
          }
        }}
        stopPropagation={false}
        preventDefault={false}
        className="hover:text-link text-inherit no-underline truncate flex-1"
      >
        <span className="truncate" data-test={`${rowData.id}-open-quick-view`}>
          {upperFirst(info.getValue() as string)}
        </span>
      </ClickableLink>
      <div className="ml-2 opacity-0 group-hover/cell:opacity-100">
        <ClickableLink
          href={url}
          onClick={(e) => {
            e.stopPropagation();
            if (entityType === EntityType.DEAL) {
              navigateToDealDetailedView(rowData.id);
            } else {
              navigate(url);
            }
          }}
          stopPropagation={true}
        >
          <IconButton data-test={`${rowData.id}-open-detailed-view`} size="md">
            <ArrowUpRight />
          </IconButton>
        </ClickableLink>
      </div>
    </div>
  );
};

export default TableNameCell;
